import React, { useState } from "react";
import ReactModal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import { FiAward, FiX, FiPlus, FiEdit2, FiBookOpen } from "react-icons/fi";
import Ijmb from "./Ijmb";
import Diploma from "./Diploma";
import NCE from "./NCE";

const BtnAlevel = ({ modules }) => {
  const [showmodal, setshowmodal] = useState(false);
  const [data, setdata] = useState("");
  const [qualification, setqualification] = useState("");

  const _close = () => {
    setdata(null);
    setqualification(null);
    setshowmodal(false);
  };

  const _show = () => {
    setqualification("IJMB");
    setdata({ type: "IJMB" });
    setshowmodal(true);
  };

  // Define qualification types with their metadata
  const qualificationTypes = [
    {
      value: "IJMB",
      label: "IJMB",
      description: "Interim Joint Matriculation Board",
      icon: <FiBookOpen className="w-5 h-5" />,
    },
    {
      value: "NCE",
      label: "NCE",
      description: "Nigerian Certificate in Education",
      icon: <FiAward className="w-5 h-5" />,
    },
    {
      value: "Diploma",
      label: "Diploma/Advance Diploma",
      description: "National Diploma or Advance National Diploma",
      icon: <FiAward className="w-5 h-5" />,
    },
  ];

  return (
    <div>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={_close}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full min-w-[60%] max-w-5xl max-h-[90vh] overflow-y-auto"
          onContextMenu={(e) => e.stopPropagation()}
        >
            {/* Gradient Header */}
            <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-8 text-white rounded-t-xl">
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <FiAward className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">A-Level Qualifications</h2>
                    <p className="text-primary-100 mt-1">
                      Enter your higher education qualification details
                    </p>
                  </div>
                </div>
                <button
                  onClick={_close}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              {/* Decorative gradient orbs */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-400 rounded-full blur-3xl opacity-20 -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-300 rounded-full blur-3xl opacity-20 -ml-24 -mb-24" />
            </div>

            {/* Qualification Type Tabs */}
            <div className="border-b border-neutral-200">
              <div className="flex">
                {qualificationTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      setdata({ type: type.value });
                      setqualification(type.value);
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                      qualification === type.value
                        ? "text-primary-600 border-primary-600 bg-primary-50/50"
                        : "text-neutral-500 border-transparent hover:text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
                    }`}
                  >
                    {type.icon}
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Form Components Based on Selection */}
              <AnimatePresence mode="wait">
                {qualification === "IJMB" && (
                  <motion.div
                    key="ijmb"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Ijmb setdata={setdata} data={data} onClose={_close} />
                  </motion.div>
                )}
                {qualification === "Diploma" && (
                  <motion.div
                    key="diploma"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Diploma setdata={setdata} data={data} onClose={_close} />
                  </motion.div>
                )}
                {qualification === "NCE" && (
                  <motion.div
                    key="nce"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <NCE setdata={setdata} data={data} onClose={_close} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
      </ReactModal>

      {/* Add Button */}
      {!modules && (
        <button
          type="button"
          onClick={_show}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-xl shadow-sm transition-all"
        >
          <FiPlus className="w-4 h-4" />
          Add A-Level
        </button>
      )}

      {/* Edit Button (when modules prop is true) */}
      {modules && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={_show}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
          aria-label="Edit A-Level qualification"
        >
          <FiEdit2 className="w-4 h-4" />
          <span className="text-sm font-medium">Edit</span>
        </motion.button>
      )}
    </div>
  );
};

export default BtnAlevel;
